/* ============================================================================
   Slide 1 — "What is Dreamfulness?" — SHARED CONTENT (single source of truth)
   Read by both the desktop layout (HTLDSlide1Inner) and the mobile layout
   (HTLDSlide1Mobile). Edit copy ONLY here — English and German side by side;
   the German object must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent1 = "#378add";

/** A rich-text segment: plain, bold, or emphasised. */
export interface Seg { t: string; b?: boolean; em?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Introduction",
    breadcrumbAccent: "What is Dreamfulness?",
    headerTitle: "Dreamfulness",
    titlePlain: "What is",
    titleAccent: "Dreamfulness?",
    subtitle: "Wellness Via Lucid Dreaming",
    whatItIsLabel: "What it is",
    approachLabel: "The unique approach",
    notLabel: "What it is not",
  },
  whatItIs: [
    { t: "Dreamfulness uses the dream state as a direct doorway into the subconscious mind. By becoming " },
    { t: "conscious within a dream", b: true },
    { t: ", you can observe, engage with, and transform the hidden patterns, memories, and emotions that shape your waking life — from the inside." },
  ] as Seg[],
  approach: [
    { t: "Dreamfulness works " },
    { t: "during REM/Dream sleep", b: true },
    { t: " — the only state where the subconscious is fully active and open to change. Rather than talking " },
    { t: "about", em: true },
    { t: " your inner world, you enter the space where it actually lives." },
  ] as Seg[],
  notList: [
    "Dream analysis or finding meaning in dream symbols",
    "Psychotherapy or a replacement for medical treatment",
    "Mystical or unscientific — experiential and practical",
    "About inducing vivid dreams for entertainment",
  ],
  stats: [
    { label: "Method", value: "Lucid Dreaming" },
    { label: "State", value: "REM Sleep" },
    { label: "Target", value: "Subconscious" },
    { label: "Approach", value: "Non-theistic" },
  ],
  canvas: {
    caption: "become aware within the dream",
  },
};

export type Slide1Content = typeof en;

const de: Slide1Content = {
  meta: {
    breadcrumbPre: "Einführung",
    breadcrumbAccent: "Was ist Dreamfulness?",
    headerTitle: "Dreamfulness",
    titlePlain: "Was ist",
    titleAccent: "Dreamfulness?",
    subtitle: "Wohlbefinden durch luzides Träumen",
    whatItIsLabel: "Was es ist",
    approachLabel: "Der besondere Ansatz",
    notLabel: "Was es nicht ist",
  },
  whatItIs: [
    { t: "Dreamfulness nutzt den Traumzustand als direkten Zugang zum Unterbewusstsein. Wenn du " },
    { t: "im Traum bewusst wirst", b: true },
    { t: ", kannst du die verborgenen Muster, Erinnerungen und Gefühle, die dein Wachleben prägen, beobachten und von innen heraus verwandeln." },
  ],
  approach: [
    { t: "Dreamfulness wirkt " },
    { t: "im REM- bzw. Traumschlaf", b: true },
    { t: " – dem einzigen Zustand, in dem das Unterbewusstsein voll aktiv und offen für Veränderung ist. Statt " },
    { t: "über", em: true },
    { t: " deine innere Welt zu sprechen, betrittst du den Raum, in dem sie tatsächlich lebt." },
  ],
  notList: [
    "Traumdeutung oder die Suche nach Bedeutung in Traumsymbolen",
    "Psychotherapie oder Ersatz für medizinische Behandlung",
    "Mystisch oder unwissenschaftlich – sondern praktisch erfahrbar",
    "Lebhafte Träume zur Unterhaltung erzeugen",
  ],
  stats: [
    { label: "Methode", value: "Luzides Träumen" },
    { label: "Zustand", value: "REM-Schlaf" },
    { label: "Ziel", value: "Unterbewusstsein" },
    { label: "Ansatz", value: "Nicht-theistisch" },
  ],
  canvas: {
    caption: "im Traum bewusst werden",
  },
};

export const slide1Content: ByLang<Slide1Content> = { en, de };
export const useSlide1 = () => useL(slide1Content);
